import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  MapPin, Clock, UserCheck, UserX, Search, Filter,
  CheckCircle, XCircle, AlertCircle, Navigation, Shield,
  Fingerprint, MapPinOff, Wifi, Timer, Users, LocateFixed,
  BarChart3, TrendingUp, Award, AlertTriangle, ChevronLeft, ChevronRight,
  Plus, Trash2, Edit, Loader2, RefreshCw, Upload, Check, Download, ArrowRight, ArrowLeft, Camera, ShieldCheck, X, ClipboardCheck, Activity, Layers
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { safeJson } from '../lib/safeJson';
import { FaceGeoCheckIn, FaceRegistrationPanel } from '../components/FaceAttendance';
import { ComprehensiveAttendanceReport } from '../components/ComprehensiveAttendanceReport';
import { MonthlyAttendanceApproval } from '../components/MonthlyAttendanceApproval';
import { AttendancePendingApprovals } from '../components/AttendancePendingApprovals';
import { MonthlyAttendanceReport } from '../components/MonthlyAttendanceReport';
import { MonthlyStatusReport } from '../components/MonthlyStatusReport';

import { API_BASE as API_URL } from '../lib/apiBase';
/* ====================================================================
   GEO-FENCE SETTINGS TAB
   ==================================================================== */
/* GPS Fine-Tuning Test Tool */
function GPSTestTool({ campusLat, campusLng, radius, onUseLocation }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const testLocation = () => {
    setTesting(true); setResult(null);
    if (!navigator.geolocation) {
      setResult({ error: 'GPS not supported in this browser' }); setTesting(false); return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const accuracy = pos.coords.accuracy;
        const dist = haversineDistance(lat, lng, campusLat, campusLng);
        setResult({
          lat, lng, accuracy: Math.round(accuracy),
          distance: Math.round(dist),
          inside: dist <= radius,
        });
        setTesting(false);
      },
      (err) => {
        setResult({ error: `GPS Error: ${err.message}. Enable location in browser/phone settings.` });
        setTesting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="gps-test-tool">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Navigation className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-sm text-slate-800">GPS Fine-Tuning Tool</span>
          </div>
          <Button size="sm" variant="outline" onClick={testLocation} disabled={testing} data-testid="test-gps-btn">
            {testing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <MapPin className="w-3 h-3 mr-1" />}
            {testing ? 'Getting Location...' : 'Test My Location'}
          </Button>
        </div>
        <p className="text-xs text-slate-500">Stand at the center of your campus and click "Test My Location" to capture the exact GPS coordinates. You can then use them as the campus center.</p>

        {result && !result.error && (
          <div className={`rounded-lg p-3 space-y-2 ${result.inside ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-2">
              {result.inside
                ? <CheckCircle className="w-4 h-4 text-green-600" />
                : <XCircle className="w-4 h-4 text-red-600" />}
              <span className={`text-sm font-semibold ${result.inside ? 'text-green-800' : 'text-red-800'}`}>
                {result.inside ? 'Inside Geo-Fence' : 'Outside Geo-Fence'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-500">Your Latitude:</span> <strong>{result.lat.toFixed(6)}</strong></div>
              <div><span className="text-slate-500">Your Longitude:</span> <strong>{result.lng.toFixed(6)}</strong></div>
              <div><span className="text-slate-500">Distance from center:</span> <strong>{result.distance}m</strong></div>
              <div><span className="text-slate-500">GPS Accuracy:</span> <strong>{result.accuracy}m</strong></div>
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => { onUseLocation(result.lat, result.lng); toast.success('Coordinates updated! Click Save to apply.'); }}
                data-testid="use-my-location-btn">
                <MapPin className="w-3 h-3 mr-1" /> Use My Location as Campus Center
              </Button>
            </div>
          </div>
        )}
        {result?.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
            <AlertCircle className="w-4 h-4 inline mr-1" /> {result.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GeoFenceSettings({ entityId }) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entityId) fetchConfig();
  }, [entityId]);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/geofence/${entityId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) setConfig(await res.json());
    } catch (e) { console.error(e); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/geofence/${entityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(config),
        credentials: 'include'
      });
      if (res.ok) { setConfig(await res.json()); toast.success('Geo-fence configuration saved'); }
      else toast.error('Failed to save configuration');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (!config) return <div className="animate-pulse h-64 bg-slate-100 rounded-lg" />;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="geofence-settings">
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6 space-y-8">
          {/* Header Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              <span className="text-lg font-semibold text-slate-800">Geo-Fence Configuration</span>
            </div>
            <Switch
              data-testid="geofence-enabled-toggle"
              checked={config.enabled}
              onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
            />
          </div>

          {/* Visual Indicator */}
          <div className="flex justify-center py-4">
            <div className="relative w-40 h-40">
              <div className={`absolute inset-0 rounded-full border-2 border-dashed ${config.enabled ? 'border-blue-300' : 'border-slate-200'} animate-pulse`} />
              <div className={`absolute inset-4 rounded-full border-2 border-dashed ${config.enabled ? 'border-blue-400' : 'border-slate-200'}`} />
              <div className={`absolute inset-10 rounded-full ${config.enabled ? 'bg-blue-50' : 'bg-slate-50'} flex items-center justify-center`}>
                <Shield className={`w-8 h-8 ${config.enabled ? 'text-blue-600' : 'text-slate-300'}`} />
              </div>
            </div>
          </div>

          {/* Lat/Lng */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-blue-700">Latitude</Label>
              <Input
                data-testid="geofence-latitude"
                type="number" step="0.0001"
                value={config.latitude || ''}
                onChange={(e) => setConfig({ ...config, latitude: parseFloat(e.target.value) || 0 })}
                disabled={!config.enabled}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-blue-700">Longitude</Label>
              <Input
                data-testid="geofence-longitude"
                type="number" step="0.0001"
                value={config.longitude || ''}
                onChange={(e) => setConfig({ ...config, longitude: parseFloat(e.target.value) || 0 })}
                disabled={!config.enabled}
              />
            </div>
          </div>

          {/* Radius Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wifi className="w-4 h-4 text-slate-600" />
                <span className="font-medium text-slate-700">Radius</span>
              </div>
              <span className="text-sm font-semibold bg-slate-100 px-3 py-1 rounded-full" data-testid="radius-value">{config.radius}m</span>
            </div>
            <Slider
              data-testid="geofence-radius-slider"
              min={50} max={500} step={10}
              value={[config.radius]}
              onValueChange={([v]) => setConfig({ ...config, radius: v })}
              disabled={!config.enabled}
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>50m</span><span>500m</span>
            </div>
          </div>

          {/* Strict Mode */}
          <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-800">Strict Mode</p>
              <p className="text-sm text-slate-500">Reject check-in outside geofence</p>
            </div>
            <Switch
              data-testid="strict-mode-toggle"
              checked={config.strict_mode}
              onCheckedChange={(v) => setConfig({ ...config, strict_mode: v })}
              disabled={!config.enabled}
            />
          </div>

          {/* Grace Period */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Timer className="w-4 h-4 text-slate-600" />
                <span className="font-medium text-slate-700">Grace Period</span>
              </div>
              <span className="text-sm font-semibold bg-slate-100 px-3 py-1 rounded-full" data-testid="grace-period-value">{config.grace_period} min</span>
            </div>
            <Slider
              data-testid="geofence-grace-slider"
              min={0} max={30} step={1}
              value={[config.grace_period]}
              onValueChange={([v]) => setConfig({ ...config, grace_period: v })}
              disabled={!config.enabled}
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>0 min</span><span>30 min</span>
            </div>
          </div>

          {/* Work Hours */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-600">Work Start Time</Label>
              <Input
                data-testid="work-start-time"
                type="time" value={config.work_start_time || '09:00'}
                onChange={(e) => setConfig({ ...config, work_start_time: e.target.value })}
                disabled={!config.enabled}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-600">Work End Time</Label>
              <Input
                data-testid="work-end-time"
                type="time" value={config.work_end_time || '17:30'}
                onChange={(e) => setConfig({ ...config, work_end_time: e.target.value })}
                disabled={!config.enabled}
              />
            </div>
          </div>

          {/* Attendance Timing Rules */}
          <div className="border-t pt-6 mt-4">
            <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 mb-4">
              <Timer className="w-5 h-5 text-[#DC2626]" /> Attendance Timing Rules
            </h3>

            {/* Check-in Window */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium text-green-700">Check-in Window Start</Label>
                <Input type="time" value={config.checkin_start || '07:00'} onChange={(e) => setConfig({ ...config, checkin_start: e.target.value })} disabled={!config.enabled} data-testid="checkin-start" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-green-700">Check-in Window End</Label>
                <Input type="time" value={config.checkin_end || '11:00'} onChange={(e) => setConfig({ ...config, checkin_end: e.target.value })} disabled={!config.enabled} data-testid="checkin-end" />
              </div>
            </div>

            {/* Check-out Window */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium text-orange-700">Check-out Window Start</Label>
                <Input type="time" value={config.checkout_start || '16:00'} onChange={(e) => setConfig({ ...config, checkout_start: e.target.value })} disabled={!config.enabled} data-testid="checkout-start" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-orange-700">Check-out Window End</Label>
                <Input type="time" value={config.checkout_end || '20:00'} onChange={(e) => setConfig({ ...config, checkout_end: e.target.value })} disabled={!config.enabled} data-testid="checkout-end" />
              </div>
            </div>

            {/* Late Mark & Half Day */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium text-amber-700">Late Mark After</Label>
                <Input type="time" value={config.late_mark_after || '09:15'} onChange={(e) => setConfig({ ...config, late_mark_after: e.target.value })} disabled={!config.enabled} data-testid="late-mark-after" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-red-700">Morning Half Day After</Label>
                <Input type="time" value={config.morning_half_day_after || '11:00'} onChange={(e) => setConfig({ ...config, morning_half_day_after: e.target.value })} disabled={!config.enabled} data-testid="morning-half-day" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-red-700">Evening Half Day Before</Label>
                <Input type="time" value={config.evening_half_day_before || '13:00'} onChange={(e) => setConfig({ ...config, evening_half_day_before: e.target.value })} disabled={!config.enabled} data-testid="evening-half-day" />
              </div>
            </div>

            {/* Auto Checkout */}
            <div className="bg-amber-50 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-800 text-sm">Auto Check-out Time</p>
                <p className="text-xs text-slate-500">Employees who forget to check out will be auto-checked-out at this time</p>
              </div>
              <Input type="time" value={config.auto_checkout_time || '18:00'} onChange={(e) => setConfig({ ...config, auto_checkout_time: e.target.value })} disabled={!config.enabled} className="w-32" data-testid="auto-checkout-time" />
            </div>

            <p className="text-xs text-slate-400 mt-3">
              Check-in/out outside the allowed window will require HR approval. Late mark and half day rules are applied automatically.
            </p>
          </div>

          {/* GPS Fine-Tuning Tool */}
          {config.enabled && (
            <GPSTestTool
              campusLat={config.latitude}
              campusLng={config.longitude}
              radius={config.radius}
              onUseLocation={(lat, lng) => setConfig({ ...config, latitude: lat, longitude: lng })}
            />
          )}

          {/* Save Button */}
          <Button
            data-testid="save-geofence-btn"
            onClick={saveConfig}
            disabled={saving}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


/* ====================================================================
   MY ATTENDANCE TAB (Employee self-service geo check-in)
   ==================================================================== */
function MyAttendance() {
  const [status, setStatus] = useState(null);
  const [location, setLocation] = useState(null);
  const [locError, setLocError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [history, setHistory] = useState([]);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [checkInMode, setCheckInMode] = useState('geo'); // 'geo' or 'face_geo'
  const swipeRef = useRef(null);
  const touchStartRef = useRef({ x: 0, time: 0 });

  useEffect(() => {
    fetchStatus();
    fetchHistory();
  }, []);

  // silent=true → refresh data without unmounting the subtree (keeps the
  // "Attendance Marked!" success screen visible after a check-in)
  const fetchStatus = async (silent = false) => {
    if (!silent) setFetching(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/attendance/my-status`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) setStatus(await res.json());
    } catch (e) { console.error(e); }
    finally { setFetching(false); }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/me/attendance?start_date=${format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd')}&end_date=${format(new Date(), 'yyyy-MM-dd')}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) setHistory(await res.json());
    } catch (e) { console.error(e); }
  };

  const getLocation = () => {
    setLocError(null);
    if (!navigator.geolocation) {
      setLocError('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => setLocError(err.code === 1 ? 'Location access denied. Please enable GPS.' : 'Could not get location. Try again.'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => { getLocation(); }, []);

  const handleCheckIn = async () => {
    if (!location) { toast.error('Location not available. Please enable GPS.'); getLocation(); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/attendance/geo-checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ latitude: location.latitude, longitude: location.longitude }),
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.action === 'check_in' ? 'Checked in successfully!' : 'Checked out successfully!');
        fetchStatus();
        fetchHistory();
      } else {
        toast.error(data.detail || 'Check-in failed');
      }
    } catch (e) { toast.error('Failed to check in'); }
    finally { setLoading(false); }
  };

  if (fetching) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />)}</div>;

  if (!status?.has_employee_profile) {
    return (
      <Card className="max-w-md mx-auto mt-8">
        <CardContent className="p-8 text-center">
          <UserX className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No Employee Profile</h3>
          <p className="text-sm text-slate-500 mt-2">Your account is not linked to an employee profile. Contact HR.</p>
        </CardContent>
      </Card>
    );
  }

  const isCheckedIn = status?.status === 'checked_in';
  const isCheckedOut = status?.status === 'checked_out';
  const todayRecord = status?.today_record;

  return (
    <div className="space-y-6" data-testid="my-attendance-tab">
      {/* Check-in Card */}
      <Card className="max-w-lg mx-auto shadow-lg border-0">
        <CardContent className="p-6 space-y-6">
          {/* Status Indicator */}
          <div className="text-center">
            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center ${
              isCheckedOut ? 'bg-green-50 ring-4 ring-green-200' :
              isCheckedIn ? 'bg-blue-50 ring-4 ring-blue-200' :
              'bg-slate-50 ring-4 ring-slate-200'
            }`}>
              {isCheckedOut ? <CheckCircle className="w-12 h-12 text-green-500" /> :
               isCheckedIn ? <Clock className="w-12 h-12 text-blue-500" /> :
               <Navigation className="w-12 h-12 text-slate-400" />}
            </div>
            <h2 className="text-xl font-bold mt-3 text-slate-800" data-testid="checkin-status-text">
              {isCheckedOut ? 'Day Complete' : isCheckedIn ? 'Currently Working' : 'Ready to Check In'}
            </h2>
            <p className="text-sm text-slate-500">
              {isCheckedOut ? `${todayRecord?.check_in} - ${todayRecord?.check_out} (${todayRecord?.work_hours?.toFixed(1)}h)` :
               isCheckedIn ? `Checked in at ${todayRecord?.check_in}` :
               format(new Date(), 'EEEE, MMM dd yyyy')}
            </p>
          </div>

          {/* Location Info */}
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <LocateFixed className={`w-4 h-4 ${location ? 'text-green-500' : 'text-red-400'}`} />
                <span className="text-slate-600">{location ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}` : 'Acquiring location...'}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={getLocation} data-testid="refresh-location-btn">
                <Navigation className="w-3 h-3" />
              </Button>
            </div>
            {location?.accuracy && <p className="text-xs text-slate-400">Accuracy: ~{Math.round(location.accuracy)}m</p>}
            {locError && <p className="text-xs text-red-500">{locError}</p>}
            {status?.geofence_enabled && <Badge variant="outline" className="text-xs"><Shield className="w-3 h-3 mr-1" /> Geo-fence active</Badge>}
          </div>

          {/* Face + Geo Check-in/out — buttons disable based on today's record */}
          <FaceGeoCheckIn
            todayRecord={todayRecord}
            onSuccess={() => { fetchStatus(true); fetchHistory(); }}
          />

          {todayRecord?.late_mark && <Badge className="bg-orange-100 text-orange-700 w-full justify-center">Late Arrival</Badge>}
          {todayRecord?.inside_geofence === false && <Badge className="bg-red-100 text-red-700 w-full justify-center">Outside Geofence ({todayRecord?.distance_from_campus}m away)</Badge>}
          {todayRecord?.face_verified && <Badge className="bg-emerald-100 text-emerald-700 w-full justify-center"><Check className="w-3 h-3 mr-1" /> Face Verified</Badge>}
        </CardContent>
      </Card>

      {/* Recent History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Clock className="w-5 h-5 text-blue-600" /> Recent Attendance (30 days)</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-center text-slate-500 py-4">No attendance records found</p>
          ) : (
            <div className="overflow-x-auto responsive-card-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Check In</TableHead>
                    <TableHead>Check Out</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.slice(0, 15).map(att => (
                    <TableRow key={att.attendance_id}>
                      <TableCell className="font-medium">{att.date}</TableCell>
                      <TableCell>{att.check_in || '-'}</TableCell>
                      <TableCell>{att.check_out || '-'}</TableCell>
                      <TableCell>{att.work_hours?.toFixed(1) || 0}h</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize text-xs">
                          {att.attendance_type === 'geo' && <MapPin className="w-3 h-3 mr-1" />}
                          {att.attendance_type === 'biometric' && <Fingerprint className="w-3 h-3 mr-1" />}
                          {att.attendance_type?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={att.status === 'present' ? 'badge-success' : att.status === 'absent' ? 'badge-error' : 'badge-warning'}>
                          {att.status?.replace('_', ' ')}
                        </Badge>
                        {att.late_mark && <Badge variant="outline" className="ml-1 text-orange-600 text-xs">Late</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/* ====================================================================
   BIOMETRIC/HR MARKING TAB
   ==================================================================== */
function BiometricMarking({ entityId }) {
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmps, setSelectedEmps] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('present');
  const [loading, setLoading] = useState(false);
  const [existingRecords, setExistingRecords] = useState([]);
  const [singleDialog, setSingleDialog] = useState(null);

  useEffect(() => { fetchEmployees(); }, [entityId]);
  useEffect(() => { fetchExisting(); }, [entityId, selectedDate]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/employees${entityId ? `?entity_id=${entityId}` : ''}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) setEmployees(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchExisting = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/attendance?date=${selectedDate}${entityId ? `&entity_id=${entityId}` : ''}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) setExistingRecords(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleBulkMark = async () => {
    if (selectedEmps.length === 0) { toast.error('Select at least one employee'); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const entries = selectedEmps.map(empId => ({
        employee_id: empId,
        date: selectedDate,
        status: bulkStatus,
        check_in: bulkStatus === 'present' ? '09:00:00' : null,
        check_out: bulkStatus === 'present' ? '17:30:00' : null,
        remarks: 'Biometric/HR marked'
      }));
      const res = await fetch(`${API_URL}/api/attendance/biometric/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(entries),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Marked: ${data.created} new, ${data.updated} updated`);
        setSelectedEmps([]);
        fetchExisting();
      } else toast.error('Failed to mark attendance');
    } catch (e) { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleSingleMark = async () => {
    if (!singleDialog) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/attendance/biometric`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(singleDialog),
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Attendance marked');
        setSingleDialog(null);
        fetchExisting();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed');
      }
    } catch (e) { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const filtered = employees.filter(e =>
    `${e.first_name} ${e.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.employee_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRecord = (empId) => existingRecords.find(r => r.employee_id === empId);
  const toggleEmp = (empId) => setSelectedEmps(prev => prev.includes(empId) ? prev.filter(x => x !== empId) : [...prev, empId]);
  const selectAll = () => { const unmarked = filtered.filter(e => !getRecord(e.employee_id)).map(e => e.employee_id); setSelectedEmps(unmarked); };

  return (
    <div className="space-y-6" data-testid="biometric-marking-tab">
      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-end gap-4">
        <div className="space-y-1">
          <Label className="text-sm text-slate-600">Date</Label>
          <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-44" data-testid="biometric-date" />
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search employees..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="biometric-search" />
        </div>
      </div>

      {/* Bulk Actions */}
      <Card className="border-blue-100 bg-blue-50/50">
        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Fingerprint className="w-5 h-5 text-blue-600 shrink-0" />
          <span className="text-sm font-medium text-blue-800">{selectedEmps.length} selected</span>
          <div className="flex gap-2 flex-wrap ml-auto">
            <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-unmarked">Select All Unmarked</Button>
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger className="w-32 h-8" data-testid="bulk-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="present">Present</SelectItem>
                <SelectItem value="absent">Absent</SelectItem>
                <SelectItem value="half_day">Half Day</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleBulkMark} disabled={loading || selectedEmps.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-mark-btn">
              {loading ? 'Marking...' : 'Mark Attendance'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Employee Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto responsive-card-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Check In</TableHead>
                  <TableHead>Check Out</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(emp => {
                  const record = getRecord(emp.employee_id);
                  return (
                    <TableRow key={emp.employee_id} data-testid={`bio-row-${emp.employee_id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedEmps.includes(emp.employee_id)}
                          onCheckedChange={() => toggleEmp(emp.employee_id)}
                          disabled={!!record}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{emp.first_name} {emp.last_name}</p>
                          <p className="text-xs text-slate-500">{emp.employee_id}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{emp.department}</TableCell>
                      <TableCell>
                        {record ? (
                          <Badge className={record.status === 'present' ? 'badge-success' : record.status === 'absent' ? 'badge-error' : 'badge-warning'}>
                            {record.status?.replace('_', ' ')}
                          </Badge>
                        ) : <Badge variant="outline" className="text-slate-400">Unmarked</Badge>}
                      </TableCell>
                      <TableCell>
                        {record?.attendance_type && (
                          <Badge variant="outline" className="capitalize text-xs">
                            {record.attendance_type === 'geo' && <MapPin className="w-3 h-3 mr-1" />}
                            {record.attendance_type === 'biometric' && <Fingerprint className="w-3 h-3 mr-1" />}
                            {record.attendance_type}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{record?.check_in || '-'}</TableCell>
                      <TableCell className="text-sm">{record?.check_out || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setSingleDialog({
                            employee_id: emp.employee_id,
                            date: selectedDate,
                            status: record?.status || 'present',
                            check_in: record?.check_in || '09:00:00',
                            check_out: record?.check_out || '17:30:00',
                            remarks: record?.remarks || ''
                          })}
                          data-testid={`mark-single-${emp.employee_id}`}
                        >
                          <Fingerprint className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Single Mark Dialog */}
      <Dialog open={!!singleDialog} onOpenChange={(o) => !o && setSingleDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Fingerprint className="w-5 h-5 text-blue-600" /> Mark Attendance</DialogTitle>
          </DialogHeader>
          {singleDialog && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-slate-600">Employee: <strong>{employees.find(e => e.employee_id === singleDialog.employee_id)?.first_name} {employees.find(e => e.employee_id === singleDialog.employee_id)?.last_name}</strong></p>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={singleDialog.status} onValueChange={(v) => setSingleDialog({ ...singleDialog, status: v })}>
                  <SelectTrigger data-testid="single-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="half_day">Half Day</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Check In</Label>
                  <Input type="time" value={singleDialog.check_in?.slice(0, 5)} onChange={(e) => setSingleDialog({ ...singleDialog, check_in: `${e.target.value}:00` })} data-testid="single-checkin" />
                </div>
                <div className="space-y-2">
                  <Label>Check Out</Label>
                  <Input type="time" value={singleDialog.check_out?.slice(0, 5)} onChange={(e) => setSingleDialog({ ...singleDialog, check_out: `${e.target.value}:00` })} data-testid="single-checkout" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input placeholder="Optional remarks" value={singleDialog.remarks} onChange={(e) => setSingleDialog({ ...singleDialog, remarks: e.target.value })} data-testid="single-remarks" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setSingleDialog(null)}>Cancel</Button>
                <Button onClick={handleSingleMark} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="single-mark-btn">
                  {loading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ====================================================================
   TEAM RECORDS TAB (for HR/HOD)
   ==================================================================== */
function TeamRecords({ entityId }) {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => { fetchData(); }, [entityId, selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const [attRes, empRes] = await Promise.all([
        fetch(`${API_URL}/api/attendance?date=${dateStr}${entityId ? `&entity_id=${entityId}` : ''}`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/employees${entityId ? `?entity_id=${entityId}` : ''}`, { headers, credentials: 'include' })
      ]);
      if (attRes.ok) setAttendance(await attRes.json());
      if (empRes.ok) setEmployees(await empRes.json());
    } catch (e) { console.error(e); toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const getEmployeeName = (empId) => { const e = employees.find(x => x.employee_id === empId); return e ? `${e.first_name} ${e.last_name}` : empId; };

  const filtered = attendance.filter(att => {
    const name = getEmployeeName(att.employee_id).toLowerCase();
    const matchSearch = name.includes(searchTerm.toLowerCase()) || att.employee_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || att.status === statusFilter;
    const matchType = typeFilter === 'all' || att.attendance_type === typeFilter;
    return matchSearch && matchStatus && matchType;
  });

  const stats = {
    total: employees.length,
    present: attendance.filter(a => a.status === 'present').length,
    absent: attendance.filter(a => a.status === 'absent').length,
    geo: attendance.filter(a => a.attendance_type === 'geo').length,
    biometric: attendance.filter(a => a.attendance_type === 'biometric').length,
    late: attendance.filter(a => a.late_mark).length
  };

  return (
    <div className="space-y-6" data-testid="team-records-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Staff', value: stats.total, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Present', value: stats.present, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Absent', value: stats.absent, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Geo Check-in', value: stats.geo, icon: MapPin, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Biometric', value: stats.biometric, icon: Fingerprint, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Late', value: stats.late, icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-50' },
        ].map(s => (
          <Card key={s.label} className="border-0 shadow-sm">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500">{s.label}</p>
                <p className="text-lg font-bold">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-sm">Select Date</CardTitle></CardHeader>
          <CardContent>
            <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} className="rounded-md border" data-testid="team-calendar" />
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <CardTitle className="text-lg">Attendance for {format(selectedDate, 'dd MMM yyyy')}</CardTitle>
              <div className="flex gap-2 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search..." className="pl-10 w-44" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="team-search" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-28" data-testid="team-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-28" data-testid="team-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="geo">Geo</SelectItem>
                    <SelectItem value="biometric">Biometric</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <UserCheck className="w-16 h-16 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No attendance records for this date</p>
              </div>
            ) : (
              <div className="overflow-x-auto responsive-card-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Check In</TableHead>
                      <TableHead>Check Out</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(att => (
                      <TableRow key={att.attendance_id}>
                        <TableCell>
                          <p className="font-medium text-sm">{getEmployeeName(att.employee_id)}</p>
                          <p className="text-xs text-slate-500">{att.employee_id}</p>
                        </TableCell>
                        <TableCell className="text-sm">{att.check_in || '-'}</TableCell>
                        <TableCell className="text-sm">{att.check_out || '-'}</TableCell>
                        <TableCell className="text-sm">{att.work_hours?.toFixed(1) || 0}h</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">
                            {att.attendance_type === 'geo' && <MapPin className="w-3 h-3 mr-1" />}
                            {att.attendance_type === 'biometric' && <Fingerprint className="w-3 h-3 mr-1" />}
                            {att.attendance_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {att.latitude ? (
                            <span className="flex items-center gap-1">
                              {att.inside_geofence ? <CheckCircle className="w-3 h-3 text-green-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                              {att.distance_from_campus ? `${Math.round(att.distance_from_campus)}m` : '-'}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={att.status === 'present' ? 'badge-success' : att.status === 'absent' ? 'badge-error' : 'badge-warning'}>
                            {att.status?.replace('_', ' ')}
                          </Badge>
                          {att.late_mark && <Badge variant="outline" className="ml-1 text-orange-600 text-xs">Late</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                            onClick={async () => {
                              if (!window.confirm('Delete this attendance record permanently?')) return;
                              const token = localStorage.getItem('hrms-token');
                              const res = await fetch(`${API_URL}/api/attendance/${att.attendance_id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' });
                              if (res.ok) { toast.success('Record deleted'); fetchData(); } else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Delete failed'); }
                            }}
                            data-testid={`delete-att-${att.attendance_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


/* ====================================================================
   ANALYTICS TAB (Heatmap + Punctuality + Department Summary)
   ==================================================================== */
function AttendanceAnalytics({ entityId }) {
  const [analytics, setAnalytics] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [empAnalytics, setEmpAnalytics] = useState(null);
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (entityId) fetchAnalytics(); }, [entityId, month]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/attendance/analytics?entity_id=${entityId}&month=${month}`, { headers, credentials: 'include' });
      if (res.ok) setAnalytics(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchEmployeeAnalytics = async (empId) => {
    setSelectedEmployee(empId);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/attendance/analytics/employee/${empId}?month=${month}`, { headers, credentials: 'include' });
      if (res.ok) setEmpAnalytics(await res.json());
    } catch (e) { console.error(e); }
  };

  const changeMonth = (delta) => {
    const d = new Date(month + '-01');
    d.setMonth(d.getMonth() + delta);
    setMonth(format(d, 'yyyy-MM'));
    setSelectedEmployee(null);
    setEmpAnalytics(null);
  };

  const getScoreColor = (score) => score >= 90 ? 'text-green-600' : score >= 70 ? 'text-blue-600' : score >= 50 ? 'text-orange-600' : 'text-red-600';
  const getScoreBg = (score) => score >= 90 ? 'bg-green-50 ring-green-200' : score >= 70 ? 'bg-blue-50 ring-blue-200' : score >= 50 ? 'bg-orange-50 ring-orange-200' : 'bg-red-50 ring-red-200';

  if (loading) return <div className="space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />)}</div>;

  const emps = analytics?.employees || [];
  const deptSummary = analytics?.department_summary || [];

  // Heatmap rendering helper
  const renderHeatmap = (heatmap) => {
    if (!heatmap || Object.keys(heatmap).length === 0) return <p className="text-sm text-slate-400 text-center py-4">No data for this month</p>;
    const year = parseInt(month.slice(0, 4));
    const mo = parseInt(month.slice(5, 7));
    const daysInMonth = new Date(year, mo, 0).getDate();
    const firstDow = new Date(year, mo - 1, 1).getDay();

    const cells = [];
    for (let pad = 0; pad < firstDow; pad++) cells.push(<div key={`pad-${pad}`} className="w-8 h-8" />);

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${month}-${String(d).padStart(2, '0')}`;
      const entry = heatmap[dateStr];
      let bg = 'bg-slate-100';
      let title = 'No record';
      if (entry) {
        if (entry.status === 'present' && !entry.late) { bg = 'bg-green-400'; title = `Present${entry.work_hours ? ` (${entry.work_hours.toFixed(1)}h)` : ''}`; }
        else if (entry.status === 'present' && entry.late) { bg = 'bg-orange-400'; title = `Late (${entry.check_in || ''})`; }
        else if (entry.status === 'absent') { bg = 'bg-red-400'; title = 'Absent'; }
        else if (entry.status === 'half_day') { bg = 'bg-yellow-400'; title = 'Half Day'; }
        else if (entry.status === 'on_leave') { bg = 'bg-blue-300'; title = 'On Leave'; }
      }
      cells.push(
        <div key={dateStr} title={`${dateStr}: ${title}`}
          className={`w-8 h-8 rounded-md ${bg} flex items-center justify-center text-xs font-medium ${entry ? 'text-white' : 'text-slate-400'} cursor-default hover:ring-2 hover:ring-slate-400 transition-all`}>
          {d}
        </div>
      );
    }

    return (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="w-8 h-6 flex items-center justify-center text-xs text-slate-500 font-medium">{day}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">{cells}</div>
        <div className="flex gap-3 mt-3 flex-wrap">
          {[['bg-green-400', 'Present'], ['bg-orange-400', 'Late'], ['bg-red-400', 'Absent'], ['bg-yellow-400', 'Half Day'], ['bg-blue-300', 'Leave'], ['bg-slate-100', 'No Record']].map(([c, l]) => (
            <div key={l} className="flex items-center gap-1 text-xs text-slate-500">
              <div className={`w-3 h-3 rounded-sm ${c}`} />{l}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Month Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <h2 className="text-lg font-semibold text-slate-800" data-testid="analytics-month">
            {format(new Date(month + '-01'), 'MMMM yyyy')}
          </h2>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Department Summary Cards */}
      {deptSummary.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {deptSummary.map(dept => (
            <Card key={dept.department} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`dept-card-${dept.department}`}>
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-800 text-sm mb-3">{dept.department}</h3>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-lg font-bold text-blue-600">{dept.employee_count}</p>
                    <p className="text-xs text-slate-500">Staff</p>
                  </div>
                  <div>
                    <p className={`text-lg font-bold ${getScoreColor(dept.avg_attendance_pct)}`}>{dept.avg_attendance_pct}%</p>
                    <p className="text-xs text-slate-500">Attendance</p>
                  </div>
                  <div>
                    <p className={`text-lg font-bold ${getScoreColor(dept.avg_punctuality)}`}>{dept.avg_punctuality}%</p>
                    <p className="text-xs text-slate-500">Punctuality</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Employee Leaderboard */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> Employee Scores</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto">
              {emps.sort((a, b) => (b.punctuality_score + b.attendance_percentage) - (a.punctuality_score + a.attendance_percentage)).map((emp, i) => (
                <div
                  key={emp.employee_id}
                  onClick={() => fetchEmployeeAnalytics(emp.employee_id)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 border-b border-slate-100 transition-colors ${selectedEmployee === emp.employee_id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                  data-testid={`emp-score-${emp.employee_id}`}
                >
                  <span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{emp.employee_name}</p>
                    <p className="text-xs text-slate-500">{emp.department}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold ${getScoreColor(emp.punctuality_score)}`}>{emp.punctuality_score}%</p>
                    <p className="text-xs text-slate-400">{emp.present}/{emp.total_days}d</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Heatmap + Detail Panel */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-600" />
              {empAnalytics ? `${empAnalytics.employee_name} — Monthly Heatmap` : 'Select an employee to view heatmap'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {empAnalytics ? (
              <div className="space-y-6">
                {/* Stats Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Punctuality', value: `${empAnalytics.punctuality_score}%`, color: getScoreColor(empAnalytics.punctuality_score), bg: getScoreBg(empAnalytics.punctuality_score) },
                    { label: 'Attendance', value: `${empAnalytics.attendance_percentage}%`, color: getScoreColor(empAnalytics.attendance_percentage), bg: getScoreBg(empAnalytics.attendance_percentage) },
                    { label: 'Late Arrivals', value: empAnalytics.late_count, color: empAnalytics.late_count > 3 ? 'text-red-600' : 'text-slate-700', bg: empAnalytics.late_count > 3 ? 'bg-red-50 ring-red-200' : 'bg-slate-50 ring-slate-200' },
                    { label: 'Avg Hours', value: `${empAnalytics.avg_work_hours}h`, color: 'text-slate-700', bg: 'bg-slate-50 ring-slate-200' },
                  ].map(s => (
                    <div key={s.label} className={`rounded-xl p-3 text-center ring-1 ${s.bg}`}>
                      <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Heatmap */}
                {renderHeatmap(empAnalytics.heatmap)}

                {/* Breakdown */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-2 border-t">
                  {[
                    { label: 'Present', value: empAnalytics.present, color: 'text-green-600' },
                    { label: 'Absent', value: empAnalytics.absent, color: 'text-red-600' },
                    { label: 'Half Day', value: empAnalytics.half_day, color: 'text-yellow-600' },
                    { label: 'On Leave', value: empAnalytics.on_leave, color: 'text-blue-600' },
                    { label: 'Geo Check-ins', value: empAnalytics.geo_checkins, color: 'text-indigo-600' },
                  ].map(s => (
                    <div key={s.label} className="text-center py-2">
                      <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-slate-500">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-16">
                <BarChart3 className="w-16 h-16 mx-auto text-slate-200 mb-3" />
                <p className="text-slate-500">Click an employee from the list to view their monthly heatmap and punctuality breakdown</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Performers & Chronic Absentees */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-600" /> Top Performers</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(analytics?.top_performers || []).map((tp, i) => (
              <div key={tp.employee_id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-600' : 'bg-orange-100 text-orange-700'}`}>{i + 1}</div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{tp.employee_name}</p>
                  <p className="text-xs text-slate-500">{tp.department}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-green-600">{tp.punctuality_score}%</p>
                  <p className="text-xs text-slate-400">{tp.attendance_percentage}% att.</p>
                </div>
              </div>
            ))}
            {(!analytics?.top_performers?.length) && <p className="text-center text-sm text-slate-400 py-4">No data</p>}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Needs Attention</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(analytics?.chronic_absentees || []).map((ca, i) => (
              <div key={ca.employee_id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{ca.employee_name}</p>
                  <p className="text-xs text-slate-500">{ca.department}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-red-600">{ca.attendance_percentage}%</p>
                  <p className="text-xs text-slate-400">{ca.late_count} late, {ca.absent} absent</p>
                </div>
              </div>
            ))}
            {(!analytics?.chronic_absentees?.length) && <p className="text-center text-sm text-slate-400 py-4">No data</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ====================================================================
   FACE REGISTRATION LIST (HR view in Attendance tab)
   ==================================================================== */
function FaceRegistrationList({ entityId }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [hrSearch, setHrSearch] = useState('');
  const [hrTarget, setHrTarget] = useState(null);
  const [exemptions, setExemptions] = useState([]);
  const [confirmExempt, setConfirmExempt] = useState(null); // { employeeId, name, exempt }

  useEffect(() => { fetchRegs(); fetchExemptions(); }, [entityId]);

  const fetchExemptions = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/face/exemptions`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: 'include'
      });
      if (res.ok) {
        const data = await safeJson(res);
        setExemptions(Array.isArray(data) ? data : []);
      }
    } catch {}
  };

  const applyExemption = async () => {
    const { employeeId, exempt } = confirmExempt;
    setConfirmExempt(null);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/face/exemption/${employeeId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ exempt, reason: exempt ? 'Device cannot capture face' : null })
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Update failed');
      toast.success(data.message);
      fetchExemptions();
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('hrms-token');
        const url = entityId
          ? `${API_URL}/api/employees?entity_id=${entityId}&include_descendants=true`
          : `${API_URL}/api/employees`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
        if (res.ok) {
          const data = await safeJson(res);
          setStaffList(Array.isArray(data) ? data : []);
        }
      } catch {}
    })();
  }, [entityId]);

  const fetchRegs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const url = entityId ? `${API_URL}/api/face/registrations?entity_id=${entityId}` : `${API_URL}/api/face/registrations`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' });
      if (res.ok) setRegistrations(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  const handleAction = async (regId, action) => {
    if (action === 'reject' && !window.confirm('Reject this face registration?')) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/face/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ registration_id: regId, action })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Face registration ${action}d`);
      fetchRegs();
    } catch (e) { toast.error(e.message); }
  };

  const handleDeleteOne = async (regId, employeeName) => {
    if (!window.confirm(`Delete face record for ${employeeName}? They will need to re-register before marking attendance.`)) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/face/registration/by-id/${regId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Delete failed');
      }
      toast.success('Face record deleted. Employee can now re-register.');
      fetchRegs();
    } catch (e) { toast.error(e.message); }
  };

  const handleResetAllForEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(
      `Reset ALL face records for ${employeeName}?\n\n` +
      `This deletes every pending + approved face descriptor for this employee, ` +
      `and clears their face_registered flag. Use this when face-match keeps failing ` +
      `(e.g., poor initial capture) — the employee can then re-register from Settings.`
    )) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/face/registration/${employeeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Reset failed');
      }
      toast.success(`All face records cleared for ${employeeName}. Ask them to re-register from Settings > Security.`);
      fetchRegs();
    } catch (e) { toast.error(e.message); }
  };

  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const fetchDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/face/diagnostics`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: 'include'
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      setDiagnostics(d);
    } catch (e) { toast.error(e.message); }
    finally { setDiagLoading(false); }
  };

  const handleMergeAttendanceDuplicates = async () => {
    if (!window.confirm(
      'Fix duplicate attendance rows?\n\nWhen a check-in timed out and the staff member tried again, ' +
      'two rows were saved for the same day. This merges them into one (earliest check-in, latest check-out).'
    )) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/attendance/duplicates/merge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({})
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
    } catch (e) { toast.error(e.message); }
  };

  const handleCleanupDuplicates = async () => {
    if (!window.confirm('Run cleanup? This removes stale duplicate approved face records (keeps the latest one per employee). Useful when employees report face mismatch errors after re-registering.')) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/face/cleanup-duplicates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Cleanup failed');
      toast.success(data.message || 'Cleanup complete');
      fetchRegs();
    } catch (e) { toast.error(e.message); }
  };

  const pending = registrations.filter(r => r.status === 'pending');
  const approved = registrations.filter(r => r.status === 'approved');

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="face-registration-list">
      <AlertDialog open={!!confirmExempt} onOpenChange={(o) => { if (!o) setConfirmExempt(null); }}>
        <AlertDialogContent data-testid="face-exempt-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmExempt?.exempt
                ? `Turn OFF face verification for ${confirmExempt?.name}?`
                : `Require face verification again for ${confirmExempt?.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmExempt?.exempt
                ? 'They will be able to mark attendance with GPS location only — no face check. Use this only for staff whose phone cannot capture a face.'
                : 'They will have to register and verify their face again before they can mark attendance.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="face-exempt-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={applyExemption} data-testid="face-exempt-confirm">
              {confirmExempt?.exempt ? 'Turn off face check' : 'Require face again'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* HR can capture a face ON BEHALF of a staff member whose own phone will not
          cooperate. Registered this way it is auto-approved. */}
      <Card className="border-blue-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
            <Camera className="w-4 h-4" /> Register a staff member's face from this device
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            Use this when a staff member's own phone cannot capture their face. Sit them in front of
            this device, pick their name and capture — it is approved instantly, no further steps.
          </p>
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              placeholder="Search staff by name..."
              value={hrSearch}
              onChange={(e) => { setHrSearch(e.target.value); setHrTarget(null); }}
              data-testid="hr-face-staff-search"
            />
            {hrTarget && (
              <Button variant="outline" onClick={() => { setHrTarget(null); setHrSearch(''); }} data-testid="hr-face-clear">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
          {!hrTarget && hrSearch.length >= 2 && (
            <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 divide-y">
              {staffList
                .filter(e => `${e.first_name} ${e.last_name} ${e.employee_code || ''}`.toLowerCase().includes(hrSearch.toLowerCase()))
                .slice(0, 8)
                .map(e => (
                  <button
                    key={e.employee_id}
                    type="button"
                    onClick={() => setHrTarget(e)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                    data-testid={`hr-face-pick-${e.employee_id}`}
                  >
                    {e.first_name} {e.last_name}
                    <span className="text-xs text-slate-400"> · {e.department || '—'}</span>
                  </button>
                ))}
            </div>
          )}
          {hrTarget && (
            <div className="pt-1">
              <p className="text-sm font-medium mb-2" data-testid="hr-face-target">
                Capturing for: {hrTarget.first_name} {hrTarget.last_name}
              </p>
              <FaceRegistrationPanel key={hrTarget.employee_id} employeeId={hrTarget.employee_id} isHR />
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-800 flex-1">
                  Still not working on their phone? Turn off face verification for this person — they can then
                  mark attendance with GPS location only.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-400 text-amber-800 hover:bg-amber-100"
                  onClick={() => setConfirmExempt({ employeeId: hrTarget.employee_id, name: `${hrTarget.first_name} ${hrTarget.last_name}`, exempt: true })}
                  data-testid="hr-face-exempt-btn"
                >
                  <Shield className="w-3 h-3 mr-1" /> Face not required
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employees currently exempt from face verification */}
      {exemptions.length > 0 && (
        <Card className="border-amber-200" data-testid="face-exemption-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
              <Shield className="w-4 h-4" /> GPS-only staff (face verification off) — {exemptions.length}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {exemptions.map(e => (
              <div key={e.employee_id} className="flex items-center justify-between text-sm border-b last:border-0 pb-2 last:pb-0">
                <div>
                  <p className="font-medium">{e.first_name} {e.last_name}</p>
                  <p className="text-xs text-slate-400">
                    {e.department || '—'}{e.face_exempt_reason ? ` · ${e.face_exempt_reason}` : ''}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-slate-600"
                  onClick={() => setConfirmExempt({ employeeId: e.employee_id, name: `${e.first_name} ${e.last_name}`, exempt: false })}
                  data-testid={`hr-face-unexempt-${e.employee_id}`}
                >
                  <X className="w-3 h-3 mr-1" /> Require face again
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="stat-card">
          <CardContent className="p-3">
            <p className="text-xs text-slate-500">Total</p>
            <p className="text-xl font-bold">{registrations.length}</p>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-amber-400">
          <CardContent className="p-3">
            <p className="text-xs text-amber-600">Pending</p>
            <p className="text-xl font-bold text-amber-700">{pending.length}</p>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-green-400">
          <CardContent className="p-3">
            <p className="text-xs text-green-600">Approved</p>
            <p className="text-xl font-bold text-green-700">{approved.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approvals */}
      {pending.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-4 h-4" /> Pending Face Registrations ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pending.map(reg => (
                <div key={reg.registration_id} className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid={`pending-face-${reg.registration_id}`}>
                  <div className="flex items-center gap-3">
                    {reg.face_snapshot ? (
                      <img src={reg.face_snapshot} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-amber-300" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                        <Camera className="w-6 h-6 text-amber-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">{reg.employee_name || reg.employee_id}</p>
                      <p className="text-xs text-slate-500">{reg.department} · {reg.created_at ? new Date(reg.created_at).toLocaleDateString('en-IN') : ''}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs" onClick={() => handleAction(reg.registration_id, 'approve')} data-testid={`att-approve-${reg.registration_id}`}>
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => handleAction(reg.registration_id, 'reject')} data-testid={`att-reject-${reg.registration_id}`}>
                      <X className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Face verification health — duplicates are what make verification hang for individual staff */}
      <Card className="border-sky-200" data-testid="face-diagnostics-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-sky-600" /> Face verification health
            <span className="text-[10px] font-normal text-slate-400">(all institutes)</span>
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={fetchDiagnostics}
              data-testid="face-diagnostics-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${diagLoading ? 'animate-spin' : ''}`} /> Check
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-7 border-amber-400 text-amber-700"
              onClick={handleMergeAttendanceDuplicates} data-testid="attendance-dedupe-btn"
              title="Merges duplicate attendance rows created when a slow check-in was retried">
              <Layers className="w-3 h-3 mr-1" /> Fix duplicate attendance
            </Button>
          </div>
        </CardHeader>
        {diagnostics && (
          <CardContent className="space-y-2 text-xs">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{diagnostics.approved} registered faces</Badge>
              <Badge variant="outline">{diagnostics.pending} pending</Badge>
              <Badge className={diagnostics.duplicate_employee_count ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                {diagnostics.duplicate_employee_count} staff with duplicate records
              </Badge>
            </div>
            <p className="text-slate-600">{diagnostics.advice}</p>
            {(diagnostics.employees_with_duplicates || []).length > 0 && (
              <div className="space-y-1">
                {diagnostics.employees_with_duplicates.map(d => (
                  <div key={d.employee_id} className="flex justify-between border-b last:border-0 pb-1"
                    data-testid={`face-dup-${d.employee_id}`}>
                    <span>{d.employee_name}</span>
                    <span className="text-red-600 font-medium">{d.copies} copies</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Approved List */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-green-600" /> Registered Employees ({approved.length})
          </CardTitle>
          <Button size="sm" variant="outline" className="text-xs h-7" onClick={handleCleanupDuplicates} data-testid="face-cleanup-duplicates-btn"
            title="Removes stale duplicate face records (fixes 'face mismatch' errors after re-registration)">
            <RefreshCw className="w-3 h-3 mr-1" /> Cleanup Duplicates
          </Button>
        </CardHeader>
        <CardContent>
          {approved.length === 0 ? (
            <p className="text-center text-slate-400 py-4 text-sm">No face registrations yet</p>
          ) : (
            <div className="overflow-x-auto responsive-card-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Registered On</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approved.map(reg => (
                    <TableRow key={reg.registration_id}>
                      <TableCell className="text-sm font-medium">{reg.employee_name || reg.employee_id}</TableCell>
                      <TableCell className="text-sm text-slate-500">{reg.department || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-400">{reg.created_at ? new Date(reg.created_at).toLocaleDateString('en-IN') : '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          onClick={() => handleResetAllForEmployee(reg.employee_id, reg.employee_name || reg.employee_id)}
                          data-testid={`reset-all-face-${reg.employee_id}`}
                          title="Reset ALL face records for this employee (they can then re-register from Settings)">
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reset
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteOne(reg.registration_id, reg.employee_name || reg.employee_id)}
                          data-testid={`delete-face-${reg.registration_id}`}
                          title="Delete this specific face record only">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/* ====================================================================
   MAIN ATTENDANCE PAGE
   ==================================================================== */
export default function Attendance() {
  const { selectedEntity } = useOutletContext() || {};
  const { user } = useAuth();
  const isHR = ['super_admin', 'admin', 'hr', 'institute_admin', 'branch_admin', 'principal'].includes(user?.role);
  const isHODOrAbove = ['super_admin', 'admin', 'hr', 'hod', 'principal'].includes(user?.role);
  const entityId = selectedEntity?.entity_id;

  const defaultTab = isHR ? 'team' : 'my-attendance';

  // Tab counts:
  // - employee: My Attendance + Report (2)
  // - HOD/Principal: My Attendance + Team + Analytics + Report (4)
  // - HR/Admin/SuperAdmin: My Attendance + Team + Analytics + Report + Monthly Approval + Biometric + Geo-Fence + Face = 8
  const tabCount = isHR ? 10 : isHODOrAbove ? 5 : 3;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">Attendance Management</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">Geo-tagged check-in, biometric marking, and attendance records</p>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        <div className="w-full max-w-full overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
        <TabsList className="grid w-full min-w-[720px] md:min-w-0" style={{ gridTemplateColumns: `repeat(${tabCount}, 1fr)` }}>
          <TabsTrigger value="my-attendance" data-testid="tab-my-attendance" className="flex items-center gap-1.5">
            <MapPin className="w-4 h-4" />
            <span className="hidden sm:inline">My Attendance</span>
            <span className="sm:hidden">Check In</span>
          </TabsTrigger>
          {isHODOrAbove && (
            <TabsTrigger value="team" data-testid="tab-team-records" className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Team Records</span>
              <span className="sm:hidden">Team</span>
            </TabsTrigger>
          )}
          {isHODOrAbove && (
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Analytics</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="report" data-testid="tab-comprehensive-report" className="flex items-center gap-1.5">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Report</span>
            <span className="sm:hidden">Report</span>
          </TabsTrigger>
          <TabsTrigger value="monthly-report" data-testid="tab-monthly-report" className="flex items-center gap-1.5">
            <Timer className="w-4 h-4" />
            <span className="hidden sm:inline">Monthly Report</span>
            <span className="sm:hidden">Monthly</span>
          </TabsTrigger>
          {isHODOrAbove && (
            <TabsTrigger value="status-report" data-testid="tab-status-report" className="flex items-center gap-1.5">
              <Timer className="w-4 h-4" />
              <span className="hidden sm:inline">Status Report</span>
              <span className="sm:hidden">Status</span>
            </TabsTrigger>
          )}
          {isHR && (
            <>
              <TabsTrigger value="pending-approvals" data-testid="tab-pending-approvals" className="flex items-center gap-1.5">
                <ClipboardCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Approvals</span>
                <span className="sm:hidden">Pending</span>
              </TabsTrigger>
              <TabsTrigger value="monthly-approval" data-testid="tab-monthly-approval" className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Monthly Approval</span>
                <span className="sm:hidden">Approve</span>
              </TabsTrigger>
              <TabsTrigger value="biometric" data-testid="tab-biometric" className="flex items-center gap-1.5">
                <Fingerprint className="w-4 h-4" />
                <span className="hidden sm:inline">Mark Attendance</span>
                <span className="sm:hidden">Mark</span>
              </TabsTrigger>
              <TabsTrigger value="geofence" data-testid="tab-geofence" className="flex items-center gap-1.5">
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Geo-Fence</span>
                <span className="sm:hidden">Geo</span>
              </TabsTrigger>
              <TabsTrigger value="face-mgmt" data-testid="tab-face-mgmt" className="flex items-center gap-1.5">
                <Camera className="w-4 h-4" />
                <span className="hidden sm:inline">Face ID</span>
                <span className="sm:hidden">Face</span>
              </TabsTrigger>
            </>
          )}
        </TabsList>
        </div>

        <TabsContent value="my-attendance" className="mt-6">
          <MyAttendance />
        </TabsContent>

        {isHODOrAbove && (
          <TabsContent value="team" className="mt-6">
            <TeamRecords entityId={entityId} />
          </TabsContent>
        )}

        {isHODOrAbove && (
          <TabsContent value="analytics" className="mt-6">
            <AttendanceAnalytics entityId={entityId} />
          </TabsContent>
        )}

        <TabsContent value="report" className="mt-6">
          <ComprehensiveAttendanceReport entityId={entityId} currentUser={user} />
        </TabsContent>

        <TabsContent value="monthly-report" className="mt-6">
          <MonthlyAttendanceReport entityId={entityId} currentUser={user} />
        </TabsContent>

        {isHODOrAbove && (
          <TabsContent value="status-report" className="mt-6">
            <MonthlyStatusReport entityId={entityId} />
          </TabsContent>
        )}

        {isHR && (
          <>
            <TabsContent value="pending-approvals" className="mt-6">
              <AttendancePendingApprovals entityId={entityId} />
            </TabsContent>
            <TabsContent value="monthly-approval" className="mt-6">
              <MonthlyAttendanceApproval entityId={entityId} />
            </TabsContent>
            <TabsContent value="biometric" className="mt-6">
              <BiometricMarking entityId={entityId} />
            </TabsContent>
            <TabsContent value="geofence" className="mt-6">
              <GeoFenceSettings entityId={entityId} />
            </TabsContent>
            <TabsContent value="face-mgmt" className="mt-6">
              <FaceRegistrationList entityId={entityId} />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
